import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PublicLayout } from '@/layouts/PublicLayout';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { FeatureRoute } from '@/routes/FeatureRoute';
import { ComingSoon } from '@/components/system/ComingSoon';
import { RouteErrorBoundary } from '@/routes/RouteErrorBoundary';
import { GuestRoute, ProtectedRoute } from '@/routes/ProtectedRoute';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { AccountDashboardPage } from '@/pages/account/AccountDashboardPage';
import { ShopPage } from '@/pages/shop/ShopPage';
import { ProductDetailPage } from '@/pages/shop/ProductDetailPage';
import { CartPage } from '@/pages/shop/CartPage';
import { CheckoutPage } from '@/pages/checkout/CheckoutPage';
import { OrderConfirmationPage } from '@/pages/orders/OrderConfirmationPage';
import { OrdersPage } from '@/pages/orders/OrdersPage';
import { AccountLayout } from '@/layouts/AccountLayout';
import { AddressesPage } from '@/pages/account/AddressesPage';
import { ProfilePage } from '@/pages/account/ProfilePage';

/**
 * Application routes — BUILD-PLAN §56 public page structure, §48 SEO-friendly
 * URLs (/personalised-gifts, /luxury-gift-boxes rather than /product?id=182).
 *
 * Every route in the blueprint is declared here from the start, each wrapped
 * in FeatureRoute. That is what makes §97 work: a module that is not built
 * yet resolves to a branded Coming Soon page at a real, linkable, indexable
 * URL, and switching its flag to `enabled` later activates it without any
 * routing change.
 *
 * Routes whose page component does not exist yet render <ComingSoon/> as their
 * element. This is deliberate and not a placeholder in the §71 sense: the URL,
 * the guard, the layout and the branded state are all genuinely wired — only
 * the enabled-state content is outstanding.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <HomePage /> },

      // --- Catalogue (§10) ---
      {
        path: 'shop',
        element: (
          <FeatureRoute feature="store" maintenanceScope="store">
            <ShopPage />
          </FeatureRoute>
        ),
      },
      {
        // §48 clean product URLs: /products/glanz-luxe-gift-box
        path: 'products/:slug',
        element: (
          <FeatureRoute feature="store" maintenanceScope="store">
            <ProductDetailPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'search',
        element: (
          <FeatureRoute feature="store" maintenanceScope="store">
            <ComingSoon featureName="Search" />
          </FeatureRoute>
        ),
      },
      {
        path: 'occasions',
        element: (
          <FeatureRoute feature="store" maintenanceScope="store">
            <ComingSoon featureName="Occasions" />
          </FeatureRoute>
        ),
      },
      {
        path: 'personalised-gifts',
        element: (
          <FeatureRoute feature="personalisation" maintenanceScope="store">
            <ComingSoon featureName="Personalised Gifts" />
          </FeatureRoute>
        ),
      },
      {
        path: 'luxury-gift-boxes',
        element: (
          <FeatureRoute feature="store" maintenanceScope="store">
            <ComingSoon featureName="Luxury Gift Boxes" />
          </FeatureRoute>
        ),
      },

      // --- Commerce (§23/§24) ---
      {
        path: 'cart',
        element: (
          <FeatureRoute feature="cart" maintenanceScope="store">
            <CartPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'checkout',
        element: (
          <FeatureRoute feature="checkout" maintenanceScope="checkout">
            <CheckoutPage />
          </FeatureRoute>
        ),
      },

      /*
       | Confirmation is deliberately NOT behind ProtectedRoute: a guest must
       | be able to see the order they just placed. The order itself is only
       | readable via navigation state or, for a signed-in customer, an
       | authorised fetch — so nothing is disclosed by the route being open.
       */
      {
        path: 'orders/:orderNumber/confirmation',
        element: (
          <FeatureRoute feature="checkout" maintenanceScope="checkout">
            <OrderConfirmationPage />
          </FeatureRoute>
        ),
      },

      // --- Gifting services (§18/§19/§20/§21) ---
      {
        path: 'bespoke-gifting',
        element: (
          <FeatureRoute feature="bespoke_gifting">
            <ComingSoon featureName="Bespoke Gifting" />
          </FeatureRoute>
        ),
      },
      {
        path: 'corporate-gifting',
        element: (
          <FeatureRoute feature="corporate_gifting">
            <ComingSoon featureName="Corporate Gifting" />
          </FeatureRoute>
        ),
      },
      {
        path: 'gift-cards',
        element: (
          <FeatureRoute feature="gift_cards">
            <ComingSoon featureName="Gift Cards" />
          </FeatureRoute>
        ),
      },
      {
        path: 'gift-registry',
        element: (
          <FeatureRoute feature="gift_registry">
            <ComingSoon featureName="Gift Registry" />
          </FeatureRoute>
        ),
      },

      // --- Customer (§58) ---
      {
        path: 'wishlist',
        element: (
          <FeatureRoute feature="wishlist">
            <ComingSoon featureName="Wishlist" />
          </FeatureRoute>
        ),
      },
      /*
       | Account routes are doubly gated: the feature flag decides whether the
       | area exists at all, then ProtectedRoute decides whether this visitor
       | may see it. Order matters — a signed-out visitor reaching a disabled
       | area should see Coming Soon, not a sign-in prompt for something that
       | is not open yet.
       */
      /*
       | The account area shares one layout, so the section rail is always in
       | the same place and the current screen is always marked. Child routes
       | are relative to it rather than another top-level `account/*` catch —
       | which is what previously sent every sub-page to Coming Soon even
       | though the screens existed.
       */
      {
        path: 'account',
        element: (
          <FeatureRoute feature="customer_accounts" maintenanceScope="accounts">
            <ProtectedRoute>
              <AccountLayout />
            </ProtectedRoute>
          </FeatureRoute>
        ),
        children: [
          { index: true, element: <AccountDashboardPage /> },
          { path: 'addresses', element: <AddressesPage /> },
          { path: 'profile', element: <ProfilePage /> },
          // Anything else under /account is genuinely not built yet.
          { path: '*', element: <ComingSoon featureName="Account" /> },
        ],
      },
      {
        path: 'orders',
        element: (
          <FeatureRoute feature="orders" maintenanceScope="accounts">
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          </FeatureRoute>
        ),
      },
      {
        path: 'track/:orderNumber?',
        element: (
          <FeatureRoute feature="live_tracking" maintenanceScope="shipping">
            <ComingSoon featureName="Order Tracking" />
          </FeatureRoute>
        ),
      },

      // --- Content & legal (§44 policy centre) ---
      { path: 'about', element: <ComingSoon featureName="About" /> },
      { path: 'contact', element: <ComingSoon featureName="Contact" /> },
      { path: 'faq', element: <ComingSoon featureName="FAQ" /> },
      { path: 'policies/:slug', element: <ComingSoon featureName="Policies" /> },
      {
        path: 'journal',
        element: (
          <FeatureRoute feature="blog">
            <ComingSoon featureName="Journal" />
          </FeatureRoute>
        ),
      },

      // Legacy/alternate path kept as a redirect so no inbound link 404s.
      { path: 'home', element: <Navigate to="/" replace /> },

      { path: '*', element: <NotFoundPage /> },
    ],
  },

  /*
   |--------------------------------------------------------------------------
   | Authentication
   |--------------------------------------------------------------------------
   |
   | Outside PublicLayout on purpose: these are focused, full-screen tasks
   | (§25). Surrounding them with shop navigation and a footer invites
   | abandonment mid-signup, and the bottom navigation would compete with the
   | form's own primary action.
   |
   | Gated by `customer_accounts` so that switching the flag off closes the
   | sign-in door too, rather than leaving a working login for an area that
   | no longer exists.
   */
  {
    path: '/login',
    errorElement: <RouteErrorBoundary />,
    element: (
      <FeatureRoute feature="customer_accounts" maintenanceScope="accounts">
        <GuestRoute>
          <LoginPage />
        </GuestRoute>
      </FeatureRoute>
    ),
  },
  {
    path: '/register',
    errorElement: <RouteErrorBoundary />,
    element: (
      <FeatureRoute feature="customer_accounts" maintenanceScope="accounts">
        <GuestRoute>
          <RegisterPage />
        </GuestRoute>
      </FeatureRoute>
    ),
  },
  {
    path: '/forgot-password',
    errorElement: <RouteErrorBoundary />,
    element: (
      <FeatureRoute feature="customer_accounts" maintenanceScope="accounts">
        <GuestRoute>
          <ForgotPasswordPage />
        </GuestRoute>
      </FeatureRoute>
    ),
  },
  {
    path: '/reset-password',
    errorElement: <RouteErrorBoundary />,
    element: (
      <FeatureRoute feature="customer_accounts" maintenanceScope="accounts">
        <GuestRoute>
          <ResetPasswordPage />
        </GuestRoute>
      </FeatureRoute>
    ),
  },
  {
    /*
     | Not wrapped in GuestRoute: the link is opened from an email and must
     | work whether or not that browser has a session. The signed URL is the
     | authorisation.
     */
    path: '/verify-email',
    errorElement: <RouteErrorBoundary />,
    element: (
      <FeatureRoute feature="customer_accounts" maintenanceScope="accounts">
        <VerifyEmailPage />
      </FeatureRoute>
    ),
  },
]);
